import { useMemo } from "react";
import { captureException } from "@sentry/react";
import useLandingPageIds from "../../../hooks/useLandingPageIds";
import { useSafeSessionStorageState } from "../../../hooks/useSafeStorageState";
import { z } from "zod";

export const QUESTIONNAIRE_ANSWERS = "questionnaireAnswers";

export const questionnaireAnswersSchema = z.object({
  prescreeningStatus: z.enum(["passed", "failed"]).optional(),
  questionAnswers: z
    .record(z.union([z.string(), z.array(z.string())]))
    .optional(),
  // BMI calculated during prescreening (used for conditional trial routing)
  bmi: z.number().nullable().optional(),
  // Multi-study matching fields
  matchedStudyId: z.string().optional(),
  matchedStudyTrialId: z.string().optional(),
  // Multi-study cascade tracking (for debug panel)
  multiStudyCascade: z
    .object({
      eligibleStudyIds: z.array(z.string()), // All location-eligible studies
      currentStudyId: z.string().optional(), // Study currently being attempted
      failedStudyIds: z.array(z.string()), // Studies that user was disqualified from
      currentStudyIndex: z.number(), // 0-based index of current study
    })
    .optional(),
});

// Derive TypeScript type from the Zod schema
export type QuestionnaireAnswers = z.infer<typeof questionnaireAnswersSchema>;

/**
 * Custom hook to manage questionnaire answers state with sessionStorage to persist between screens
 * Uses a unique composite key from the landing page IDs to keep the data isolated between routes
 * @returns [answers, setAnswers, clearAnswers] - State, update function, and clear function
 */
export function useQuestionnaireAnswersState(): [
  QuestionnaireAnswers,
  (answers: QuestionnaireAnswers) => void,
  () => void,
] {
  const { variantId, masterId } = useLandingPageIds();

  // Create a unique composite key from masterId and variantId
  // Throw an error if both IDs are undefined
  if (!masterId && !variantId) {
    throw new Error(
      "Invariant violation: both masterId and variantId are undefined",
    );
  }

  const uniqueKey = [masterId, variantId].filter(Boolean).join(":");
  const storageKey = `${QUESTIONNAIRE_ANSWERS}_${uniqueKey}`;

  const [answers, setAnswers] = useSafeSessionStorageState<QuestionnaireAnswers>(
    storageKey,
    {
      defaultValue: {},
      onError: (error) => {
        captureException(error);
      },
      listenStorageChange: true,
    },
  );

  const setValidatedAnswers = (newAnswers: QuestionnaireAnswers) => {
    try {
      const validatedAnswers = questionnaireAnswersSchema.parse(newAnswers);
      setAnswers(validatedAnswers);
    } catch (error) {
      console.error("Validation error:", error);
      captureException(error, {
        tags: {
          validation_error: "questionnaire_answers",
        },
        extra: {
          answers: JSON.stringify(newAnswers),
        },
      });
      setAnswers(newAnswers);
    }
  };

  // Function to clear answers from sessionStorage
  const clearAnswers = () => {
    setAnswers({});
  };

  return useMemo(
    () => [answers || {}, setValidatedAnswers, clearAnswers],
    [answers, setValidatedAnswers, clearAnswers],
  );
}
